import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import AccountSelect from "@/components/AccountSelect";

const ACCRUAL_CATS = ["Maaş", "Mesai", "Prim", "Yemek/Yol", "İkramiye", "Diğer Hakediş"];
const PAYMENT_CATS = ["Maaş Ödemesi", "Avans", "Kesinti", "Diğer Ödeme"];
const todayISO = () => new Date().toISOString().slice(0, 10);

export default function StaffTxnForm({ open, onClose, type, staff, defaultStaffId, onSaved, editItem }) {
  const isPay = type === "payment";
  const cats = isPay ? PAYMENT_CATS : ACCRUAL_CATS;
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editItem) setForm({ staff_id: editItem.staff_id, category: editItem.category, amount: String(editItem.amount), method: editItem.method || "Nakit", description: editItem.description || "", date: editItem.date, account_id: editItem.account_id || "" });
    else setForm({ staff_id: defaultStaffId || staff[0]?.id || "", category: cats[0], amount: "", method: "Nakit", description: "", date: todayISO(), account_id: "" });
    // eslint-disable-next-line
  }, [open, type, editItem]);

  const save = async () => {
    if (!form.staff_id || !(parseFloat(form.amount) > 0)) { toast.error("Personel ve tutar gerekli"); return; }
    setSaving(true);
    try {
      const body = { ...form, type, amount: parseFloat(form.amount), method: isPay && form.category !== "Kesinti" ? form.method : "", account_id: isPay && form.category !== "Kesinti" ? form.account_id || null : null };
      if (editItem) await api.put(`/staff-ledger/${editItem.id}`, body); else await api.post("/staff-ledger", body);
      toast.success(editItem ? "İşlem güncellendi" : isPay ? "Ödeme kaydedildi" + (form.category !== "Kesinti" ? " ve PERSONEL maliyetine işlendi" : "") : "Hakediş kaydedildi");
      onClose(); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-heading">{isPay ? "Personele Ödeme" : "Hakediş / Tahakkuk"}{editItem && <span className="text-sm font-normal text-muted-foreground"> — Düzenle</span>}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <F label="Personel">
            <Select value={form.staff_id || ""} onValueChange={(v) => setForm({ ...form, staff_id: v })}>
              <SelectTrigger data-testid="staff-txn-staff"><SelectValue placeholder="Seçin" /></SelectTrigger>
              <SelectContent>{staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <div className="grid grid-cols-2 gap-4">
            <F label="Tür">
              <Select value={form.category || cats[0]} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="staff-txn-category"><SelectValue /></SelectTrigger>
                <SelectContent>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Tutar (₺)"><Input type="number" value={form.amount || ""} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="staff-txn-amount" className="font-mono" placeholder="0" /></F>
            <F label="Tarih"><Input type="date" value={form.date || ""} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="staff-txn-date" className="font-mono" /></F>
            {isPay && form.category !== "Kesinti" && (
              <F label="Ödendiği Hesap">
                <AccountSelect value={form.account_id} onChange={(id, a) => setForm({ ...form, account_id: id, method: a?.name || form.method })} testId="staff-txn-method" />
              </F>
            )}
          </div>
          <F label="Açıklama"><Input value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="staff-txn-description" placeholder="Opsiyonel" /></F>
          {isPay && <p className="text-xs text-muted-foreground">{form.category === "Kesinti" ? "Kesinti borcu düşürür, nakit çıkışı yaratmaz (maliyete yansımaz)." : "Ödeme otomatik olarak PERSONEL maliyet kategorisine eklenir."}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>İptal</Button>
          <Button onClick={save} disabled={saving} data-testid="staff-txn-save" className="gap-2">{saving && <Loader2 className="h-4 w-4 animate-spin" />} {editItem ? "Değişiklikleri Kaydet" : "Kaydet"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const F = ({ label, children }) => (
  <div className="space-y-2">
    <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</Label>
    {children}
  </div>
);
